import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";

const DISMISS_KEY = "famfeel_week_review_dismissed";

function fmtDay(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { weekday: "long" });
}

function isSundayEvening() {
  const d = new Date();
  return d.getDay() === 0 && d.getHours() >= 17; // Sunday 5pm+
}

function weekKey() {
  // ISO week marker — one-per-week dismissal
  const d = new Date();
  const onejan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
  return `${d.getFullYear()}-w${week}`;
}

export default function WeekInReviewCard() {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isSundayEvening()) return;
    if (localStorage.getItem(DISMISS_KEY) === weekKey()) return;
    api.weekReview().then((d) => {
      // Show once the family has logged check-ins on at least 5 of the past
      // 7 days — missing a day or two is fine, no pressure. Brand-new
      // families (and truly quiet weeks) still skip the card so it stays
      // meaningful.
      if (!d || (d.days_logged ?? 0) < 5) return;
      setData(d);
      setShow(true);
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, weekKey());
    setShow(false);
  };

  if (!show || !data) return null;

  const topMember = (data.per_member || [])
    .slice()
    .sort((a, b) => b.check_ins - a.check_ins)[0];

  return (
    <div
      className="ff-card p-5 md:p-6 mb-6 relative overflow-hidden"
      data-testid="week-review-card"
      style={{ background: "var(--surface)" }}
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
        data-testid="week-review-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
      <div className="label-tiny mb-2">This week at a glance</div>
      <h3 className="font-display-italic text-3xl md:text-4xl leading-tight tracking-tight mb-5">
        A week as a family.
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-5">
        <Stat label="Check-ins" value={data.total_checkins} />
        <Stat label="Green days" value={data.green_days} />
        <Stat label="Rough days" value={data.rough_days} />
        <Stat label="Walks" value={data.walks} />
      </div>

      {data.best_day && (
        <div className="label-tiny text-[color:var(--ink-2)]">
          Best day: <span className="text-[color:var(--ink)]">{fmtDay(data.best_day.date)}</span>
          {" · avg mood "}{data.best_day.avg_mood}
          {data.worst_day && data.worst_day.date !== data.best_day.date && (
            <>
              {" · "}Toughest: <span className="text-[color:var(--ink)]">{fmtDay(data.worst_day.date)}</span>
              {" · avg "}{data.worst_day.avg_mood}
            </>
          )}
        </div>
      )}
      {topMember && (
        <div className="label-tiny text-[color:var(--ink-2)] mt-1">
          Most consistent: <span className="text-[color:var(--ink)]">{topMember.avatar_emoji} {topMember.name}</span>
          {" · "}{topMember.check_ins} check-ins
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="label-tiny text-[color:var(--ink-2)]">{label}</div>
    <div className="font-display text-3xl md:text-4xl leading-none tracking-tighter mt-1">
      {value}
    </div>
  </div>
);
